import { useRef, useState, useEffect } from 'react';
import { useScroll, useMotionValueEvent, AnimatePresence, motion } from 'motion/react';
import type { SceneSection } from '../manifest';
import { useScene } from '../scene-context';
import { SceneMedia } from '../SceneMedia';
import { Particles } from '../Particles';
import { PHERAS } from './pheras';

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

interface SaptapadiPherasProps {
  section: SceneSection;
}

export function SaptapadiPheras({ section }: SaptapadiPherasProps) {
  const { pack } = useScene();
  const isMobile = useIsMobileViewport();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentVowIndex, setCurrentVowIndex] = useState(0);

  // Track scroll progress of the entire 300vh container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Map progress (0 to 1) to vow index (0 to 6)
  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const index = Math.min(6, Math.max(0, Math.floor(latest * 7)));
    if (index !== currentVowIndex) {
      setCurrentVowIndex(index);
    }
  });

  const vow = PHERAS[currentVowIndex];

  return (
    <div
      ref={containerRef}
      data-section-id={section.sectionId}
      className="relative min-h-[300vh] w-full"
      style={{ backgroundColor: pack.colors.background }}
    >
      {/* Sticky Inner Viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col items-center justify-center">
        {/* Background Media */}
        <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
          <SceneMedia assets={section.assets} mobileBehavior={section.mobileBehavior} />
        </div>

        {/* Vignette Overlay */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ background: pack.vignette }}
          aria-hidden
        />

        {/* Particles Overlay */}
        <Particles
          effect={section.assets.particleEffect}
          reduce={isMobile ? section.mobileBehavior.reduceParticles : false}
        />

        {/* Saptapadi Title Header */}
        <div className="absolute top-12 md:top-16 z-10 text-center w-full px-6 pointer-events-none">
          <h2
            className="text-3xl md:text-5xl font-bold mb-2 tracking-wide"
            style={{ color: pack.colors.text, fontFamily: pack.fonts.display }}
          >
            {section.content.title}
          </h2>
          {section.content.subtitle && (
            <p
              className="text-base md:text-xl opacity-80 italic font-medium"
              style={{
                color: pack.colors.text,
                fontFamily: pack.fonts.script ?? pack.fonts.display,
              }}
            >
              {section.content.subtitle}
            </p>
          )}
        </div>

        {/* Glassmorphic Vow Card */}
        <div
          className="w-[90%] max-w-lg p-6 md:p-10 rounded-3xl border backdrop-blur-md relative z-10 transition-colors duration-300"
          style={{
            backgroundColor: `${pack.colors.background}d9`, // slightly transparent theme background
            borderColor: `${pack.colors.primary}40`, // 25% opacity border
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentVowIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="text-center"
            >
              <p
                className="text-xs uppercase tracking-widest mb-3 font-semibold opacity-75"
                style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}
              >
                Vow {vow.number} of 7
              </p>
              <h3
                className="text-xl md:text-2xl font-bold mb-4"
                style={{ color: pack.colors.text, fontFamily: pack.fonts.display }}
              >
                {vow.name}
              </h3>
              <p
                className="text-sm md:text-base opacity-85 leading-relaxed italic"
                style={{
                  color: pack.colors.text,
                  fontFamily: pack.fonts.body,
                }}
              >
                "{vow.meaning}"
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Visual Cue for Sticky Scroll */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center opacity-65 pointer-events-none">
          <span
            className="text-[10px] uppercase tracking-widest mb-1"
            style={{ color: pack.colors.text, fontFamily: pack.fonts.body }}
          >
            Scroll to walk pheras
          </span>
          <div
            className="w-1.5 h-6 rounded-full border flex justify-center p-0.5"
            style={{ borderColor: pack.colors.text }}
          >
            <motion.div
              className="w-1 h-1.5 rounded-full"
              style={{ backgroundColor: pack.colors.primary }}
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
